import React, { useEffect, useState, useCallback } from 'react';

import { Box, Typography } from '@mui/material';

import { useAppDispatch } from 'hooks';
import { schedulesActions } from 'state/schedules';

import { ScheduleManager } from 'models';

import LightScheduleSummary from './LightSummary';
import ScheduleDetail from './Detailed';
import TLEDetails from './TLEDetails';
import RefreshButton from 'components/RefreshButton';

interface Props extends ScheduleManager.Filters {
    context: 'satellite' | 'groundstation';
}

const ScheduleSummaryList: React.FunctionComponent<Props> = ({
    context,
    ...filters
}) => {
    const dispatch = useAppDispatch();

    const [schedules, setSchedules] =
        useState<ScheduleManager.ScheduleSummary[]>();

    const [loadingSchedules, setLoadingSchedules] = useState(false);

    const [selectedScheduleId, setSelectedScheduleId] = useState<number>();

    const [selectedSchedule, setSelectedSchedule] =
        useState<ScheduleManager.Schedule>();

    const loadSchedules = useCallback(async () => {
        setLoadingSchedules(true);

        const newSchedules = await dispatch(
            schedulesActions.getScheduleSummeryList({
                ...filters,
            }),
        );

        setSchedules(newSchedules);
        setLoadingSchedules(false);
    }, [filters]);

    useEffect(() => {
        loadSchedules();
    }, []);

    useEffect(() => {
        if (selectedScheduleId) {
            dispatch(schedulesActions.getSchedule(selectedScheduleId)).then(
                (schedule) => {
                    setSelectedSchedule(schedule);
                },
            );
        } else {
            setSelectedSchedule(undefined);
        }
    }, [selectedScheduleId]);

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', pb: 1 }}>
                <RefreshButton
                    isRefreshing={loadingSchedules}
                    onClick={loadSchedules}
                />
            </Box>
            <Box
                sx={{
                    display: 'grid',
                    gap: 1,
                    gridTemplateColumns: 'auto 1fr',
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gap: 1,
                        overflowY: 'auto',
                        maxHeight: 720,
                    }}
                >
                    {schedules?.map((schedule) => (
                        <LightScheduleSummary
                            key={schedule.id}
                            schedule={schedule}
                            context={context}
                            onClick={() => {
                                setSelectedScheduleId(schedule.id);
                            }}
                        />
                    ))}
                </Box>
                <Box>
                    {selectedSchedule && (
                        <Box sx={{ display: 'grid', gap: 1 }}>
                            <ScheduleDetail
                                context={context}
                                schedule={selectedSchedule}
                            />
                            <TLEDetails schedule={selectedSchedule} />
                        </Box>
                    )}
                    {!selectedSchedule && (
                        <Typography variant="h6" align="center">
                            Click a Schedule to see more details
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

ScheduleSummaryList.displayName = 'ScheduleSummaryList';
export default ScheduleSummaryList;
